import { useState, useEffect, useReducer, useRef } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState, shouldHideDone } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { blockedBy, msLabel } from '../outline/blocked.js'
import { StartedAheadTag, PostRecordTag } from './MilestoneFlowTags.jsx'
import MilestoneSections from './MilestoneSections.jsx'
import SlackThreadPanel from './SlackThreadPanel.jsx'
import CrewInline from './CrewInline.jsx'
import ThreadNode from './ThreadNode.jsx'
import ThreadCategoryList, { ThreadCountsMeta } from './ThreadCategoryList.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { updatePhaseTimeEntries, closeOutPhase, reopenPhase, updatePhaseCustomTitle } from '../services/phases.js'
import { scaffoldPhaseMilestones } from '../services/wbs.js'
import { alertDialog, promptDialog, choiceDialog } from './Dialog.jsx'
import { getProjectThreads, threadsForPhase } from '../services/threads.js'
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(d) {
  if (!d) return ''
  const p = d.split('-')
  return MONTHS[parseInt(p[1]) - 1] + ' ' + parseInt(p[2])
}

function daysFrom(d) {
  if (!d) return null
  return Math.round((new Date(d) - Date.now()) / 86400000)
}

function getMsForPhase(ph, milestones) {
  const pname = ph.fields['Phase Name'] || ''
  // By date; a milestone with no date yet goes AFTER the dated ones, in the order its predecessors
  // chain (2026-10-26: Santiago's undated DAPC revisions sorted above the drawings they follow).
  const byId = new Map(milestones.map(m => [m.id, m]))
  const depth = new Map()
  const depthOf = (id, seen = new Set()) => {
    if (depth.has(id)) return depth.get(id)
    if (seen.has(id)) return 0
    seen.add(id)
    const preds = ((byId.get(id) || {}).fields || {})['Predecessor'] || []
    const d = preds.length ? 1 + Math.max(...preds.map(p => byId.has(p) ? depthOf(p, seen) : 0)) : 0
    depth.set(id, d); return d
  }
  return milestones
    .filter(m => (m.fields['Project Phase'] || '') === pname)
    .sort((a, b) => {
      const da = a.fields['Posted Date'] || a.fields['Latest Finish (LFT)'] || ''
      const db = b.fields['Posted Date'] || b.fields['Latest Finish (LFT)'] || ''
      if (!da !== !db) return da ? -1 : 1
      if (!da) return depthOf(a.id) - depthOf(b.id)
      return da < db ? -1 : da > db ? 1 : 0
    })
}

/* ── Threads cache loader (shared with ThreadsSection via ctx.threadCache) ── */
function ensureThreadsLoaded(projId) {
  if (!projId) return
  if (ctx.threadCache !== undefined) return // null = in-flight, [...] = loaded
  ctx.threadCache = null
  getProjectThreads(projId).then(records => {
    ctx.threadCache = records
    window.dispatchEvent(new Event('outline-render'))
  }).catch(() => {
    ctx.threadCache = []
    window.dispatchEvent(new Event('outline-render'))
  })
}

/* ── Empty/loading filler row (standard outline grid) ──────────────────── */
function FillerRow({ text, italic }) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', ...(italic ? { fontStyle: 'italic' } : {}) }}>{text}</span>
        </div>
      </div>
    </div>
  )
}

/* ── Phase Threads list content (shared: inline node + section zoom) ───── */
export function PhaseThreadsContent({ ph, milestones, projId }) {
  const threads = ctx.threadCache
  const projectName = milestones?.[0]?.fields?.['Project Name'] || ''
  if (!Array.isArray(threads)) return <FillerRow text="Loading…" />
  const list = threadsForPhase(threads, ph)
  if (list.length === 0) return <FillerRow text="No threads linked to this phase yet." italic />
  return (
    <ThreadCategoryList
      threads={list}
      projectId={projId}
      projectName={projectName}
      scopeKey={'thcat:' + ph.id}
      onZoomCategory={type => ctx.zoomToSection('sec:phase:' + ph.id + ':thcat:' + type, { root: 'schedule', phaseId: ph.id })}
    />
  )
}

/* ── PhaseSections — the two canonical phase sub-nodes ───────────────────
 *
 * Every phase expands into two outline section nodes:
 *   Milestones — sec:phase:{phId}:milestones — the phase's milestone rows
 *   Threads    — sec:phase:{phId}:threads    — threads linked to the phase
 *                via Starting/Ending Milestone (see threadsForPhase)
 *
 * Rendered as PhaseRow's children everywhere PhaseRow appears (home project
 * list, schedule root zoom) and children-only in phase zoom. Both nodes are
 * collapsed by default (wf.collapsed[key] !== false).
 */
export function PhaseSections({ ph, milestones, projId, readOnly }) {
  const phId = ph.id
  const msSecKey = 'sec:phase:' + phId + ':milestones'
  const thSecKey = 'sec:phase:' + phId + ':threads'
  const msMenu = useActionMenu()
  const thMenu = useActionMenu()

  const effProjId = projId || wf.zoomProjectId || ''
  const phaseMilestones = getMsForPhase(ph, milestones)
  const msPostedCount = phaseMilestones.filter(m => m.fields['Posted Date']).length
  const msPct = phaseMilestones.length > 0 ? Math.round(msPostedCount / phaseMilestones.length * 100) : 0
  const msBarColor = msPct === 100 ? '#1D9E75' : msPct > 0 ? '#1a6fa8' : 'var(--aj-border)'

  // Command filter (/mine, /overdue): force the Milestones node open so
  // matching milestone rows are visible (same rule as PhaseRow itself).
  const cmdFilter = wf.cmdFilter
  const msCollapsed = cmdFilter?.type === 'msIds' ? false : (wf.collapsed[msSecKey] !== false)
  const thCollapsed = wf.collapsed[thSecKey] !== false

  // Load threads on mount so the count shows on the Threads branch even when collapsed.
  // ensureThreadsLoaded is idempotent — safe to call from every PhaseSections instance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ensureThreadsLoaded(effProjId) }, [effProjId])

  const threads = ctx.threadCache
  const phaseThreads = Array.isArray(threads) ? threadsForPhase(threads, ph) : null

  function toggle(key, collapsed) {
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function zoomSection(key) {
    if (effProjId && wf.zoomProjectId !== effProjId && ctx.zoomToProject) {
      ctx.zoomToProject(effProjId)
    }
    ctx.zoomToSection(key, { root: 'schedule', phaseId: phId })
  }

  function setAllMs(expand) {
    ctx.expandCollapseDescendants(msSecKey, expand)
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function setAllThreads(expand) {
    wf.collapsed[thSecKey] = !expand
    ;(phaseThreads || []).forEach(t => { wf.collapsed['thread:' + t.id] = !expand })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function addThread() {
    window.dispatchEvent(new CustomEvent('open-create-thread-modal', {
      detail: { projectId: effProjId, phaseId: phId },
    }))
  }

  // Add a milestone to THIS phase — the new milestone adopts the phase it's created under.
  function addMilestone() {
    window.dispatchEvent(new CustomEvent('open-add-ms-modal', { detail: { phId } }))
  }

  // Scaffold this (empty) phase's milestones from the Supabase WBS template. Suggest an anchor start =
  // the project's latest milestone finish + 1 day (the root milestone has no predecessor to chain to);
  // the rest follow the template's predecessor chain. The nightly cascade re-levels from there.
  async function scaffoldFromTemplate() {
    let maxLft = ''
    ;(milestones || []).forEach(m => { const v = m.fields && m.fields['Latest Finish (LFT)']; if (v && v > maxLft) maxLft = v })
    const suggest = maxLft ? new Date(maxLft + 'T12:00:00') : new Date()
    if (maxLft) suggest.setDate(suggest.getDate() + 1)
    const def = suggest.toISOString().slice(0, 10)
    const start = await promptDialog({
      title: 'Scaffold milestones from template',
      message: 'Create this phase’s milestones from the WBS template. Pick the start date for the first milestone (the rest follow the template chain). Leave as-is to start after current work.',
      defaultValue: def, placeholder: 'YYYY-MM-DD',
    })
    if (start === null) return
    let r = await scaffoldPhaseMilestones({ phaseRecordId: phId, anchorDate: start || null })
    // A phase with no template of its own (an Additional Services phase that adds, say, Interior
    // Architecture): pick which phase's milestones to plan inside it. They stay in this phase.
    if (r && r.skipped && r.needsTemplatePhase && (r.templatePhases || []).length) {
      const pick = await choiceDialog({
        title: 'Which milestones does this phase need?',
        message: 'This phase has no template of its own. Pick the phase whose milestones it adds; they are created here, under this phase.',
        choices: r.templatePhases.map(p => ({ value: p, label: p })),
      })
      if (!pick) return
      r = await scaffoldPhaseMilestones({ phaseRecordId: phId, anchorDate: start || null, templatePhase: pick.value })
    }
    if (r.error) { await alertDialog({ title: 'Scaffold failed', message: r.error }); return }
    if (r.skipped) { await alertDialog({ title: 'Nothing to scaffold', message: r.reason || 'Skipped.' }); return }
    await alertDialog({ title: 'Milestones created', message: 'Created ' + r.created + ' milestone' + (r.created === 1 ? '' : 's') + ' starting ' + (r.anchorDate || start) + '.' })
    if (ctx.reloadProjectData) ctx.reloadProjectData(effProjId)
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <>
      {/* Milestones */}
      <div className={`wf-node wf-node--section${msCollapsed ? ' wf-node--collapsed' : ''}`} data-key={msSecKey}>
        <div className="wf-row">
          <button className="wf-triangle" onClick={e => { e.stopPropagation(); toggle(msSecKey, msCollapsed) }}>
            <span className={`wf-tri-icon ${msCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <button
            className="wf-bullet wf-bullet--section"
            title="Focus on Milestones"
            data-kbzoom="1"
            onClick={e => { e.stopPropagation(); zoomSection(msSecKey) }}
          ></button>
          <div className="wf-text-wrap">
            <span className="wf-node-text wf-node-text--section">Milestones</span>
            {phaseMilestones.length > 0 && (
              <span className="wf-meta">
                <span className="wf-cat-bar-wrap">
                  <span className="wf-cat-bar-fill" style={{ width: msPct + '%', background: msBarColor }}></span>
                </span>
                <span className="wf-cat-count">{msPostedCount}/{phaseMilestones.length}</span>
              </span>
            )}
          </div>
          <ActionMenuButton menu={msMenu} />
        </div>
        <ActionMenu menu={msMenu}>
          {!readOnly && <ActionMenuItem onClick={addMilestone}>Add milestone</ActionMenuItem>}
          {!readOnly && phaseMilestones.length === 0 && <ActionMenuItem onClick={scaffoldFromTemplate}>Scaffold from template</ActionMenuItem>}
          {!readOnly && <ActionMenuDivider />}
          <ActionMenuItem onClick={() => setAllMs(true)}>Expand all</ActionMenuItem>
          <ActionMenuItem onClick={() => setAllMs(false)}>Collapse all</ActionMenuItem>
        </ActionMenu>
        {!msCollapsed && (
          <div className="wf-children">
            {phaseMilestones.length === 0 && <FillerRow text="No milestones in this phase." italic />}
            {phaseMilestones.map(ms => (
              <MilestoneRow key={ms.id} ms={ms} phId={phId} projId={projId} readOnly={readOnly} />
            ))}
          </div>
        )}
      </div>

      {/* Threads */}
      <div className={`wf-node wf-node--section${thCollapsed ? ' wf-node--collapsed' : ''}`} data-key={thSecKey}>
        <div className="wf-row">
          <button className="wf-triangle" onClick={e => { e.stopPropagation(); toggle(thSecKey, thCollapsed) }}>
            <span className={`wf-tri-icon ${thCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <button
            className="wf-bullet wf-bullet--section"
            title="Focus on Threads"
            data-kbzoom="1"
            onClick={e => { e.stopPropagation(); zoomSection(thSecKey) }}
          ></button>
          <div className="wf-text-wrap">
            <span className="wf-node-text wf-node-text--section">Threads</span>
            {phaseThreads && (
              <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{phaseThreads.length}</span>
            )}
          </div>
          <ActionMenuButton menu={thMenu} />
        </div>
        <ActionMenu menu={thMenu}>
          <ActionMenuItem onClick={addThread}>Add thread</ActionMenuItem>
          <ActionMenuDivider />
          <ActionMenuItem onClick={() => setAllThreads(true)}>Expand all</ActionMenuItem>
          <ActionMenuItem onClick={() => setAllThreads(false)}>Collapse all</ActionMenuItem>
        </ActionMenu>
        {!thCollapsed && (
          <div className="wf-children">
            <PhaseThreadsContent ph={ph} milestones={milestones} projId={effProjId} />
          </div>
        )}
      </div>
    </>
  )
}

/** The small lock on a milestone that is waiting on a predecessor (outline/blocked.js). Hovering it
 *  says what clears it: each open predecessor, its target finish and checklist progress, and whether
 *  that predecessor is itself waiting on something. `waitingOn` = blockedBy(ms). */
export function BlockedLock({ waitingOn = [] }) {
  const ref = useRef(null)
  const [pos, setPos] = useState(null)
  function show() {
    if (!waitingOn.length || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    const w = 300
    setPos({ left: Math.max(8, Math.min(r.left - 8, window.innerWidth - w - 8)), top: r.bottom + 6, below: window.innerHeight - r.bottom > 200, bottom: window.innerHeight - r.top + 6 })
  }
  return (
    <span ref={ref} className="wf-blocked-lock-wrap" onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      <svg className="wf-blocked-lock" width="11" height="11" viewBox="0 0 16 16" fill="none" aria-label="Waiting on a predecessor">
        <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      {pos && createPortal(
        <div className="wf-blocked-pop" style={pos.below ? { left: pos.left, top: pos.top } : { left: pos.left, bottom: pos.bottom }}>
          <div className="wf-blocked-pop-head">Can't start yet</div>
          <div className="wf-blocked-pop-lead">
            Unlocks when {waitingOn.length === 1 ? 'this predecessor is' : 'these predecessors are'} posted:
          </div>
          {waitingOn.map(p => {
            const pf = p.fields || {}
            const done = parseInt(pf['Completed Checklist Items'] || 0)
            const total = done + parseInt(pf['Incomplete Checklist Items'] || 0)
            const lft = pf['Latest Finish (LFT)']
            const upstream = blockedBy(p)
            return (
              <div key={p.id} className="wf-blocked-pop-item">
                <div className="wf-blocked-pop-name">{msLabel(p)}</div>
                <div className="wf-blocked-pop-meta">
                  {[lft ? 'Target finish ' + fmtDate(lft) : 'No target finish', total ? done + ' of ' + total + ' checklist items' : null].filter(Boolean).join(' · ')}
                </div>
                {upstream.length > 0 && (
                  <div className="wf-blocked-pop-meta">Itself waiting on {upstream.map(msLabel).join(', ')}</div>
                )}
              </div>
            )
          })}
        </div>,
        document.body
      )}
    </span>
  )
}

export function MilestoneRow({ ms, phId, projId, readOnly, labelPrefix = '' }) {
  const f = ms.fields
  const msId = ms.id

  // Slack discussion state — must be before any early return (hooks rule)
  const [showPanel, setShowPanel] = useState(false)
  // (Date editing moved to the Gantt scheduler modal — single editing surface.)
  // Who is on this milestone comes from the crew index (CrewInline reads it) — a queued
  // milestone has no owner by design, so there is nothing per-row to load here.

  // Command filter: hide milestones not in the active filter set
  const cmdFilter = wf.cmdFilter
  if (cmdFilter?.type === 'msIds' && !cmdFilter.msIds.has(msId)) return null

  const isPosted = !!f['Posted Date']
  // Waiting on a predecessor (outline/blocked.js): read-only, dimmed, with a lock and what it waits on.
  const waitingOn = isPosted ? [] : blockedBy(ms)
  const isBlockedMs = waitingOn.length > 0
  const isCollapsed = wf.collapsed['ms:' + msId] !== false
  const isDirty = !!(wf.dirty && wf.dirty[msId])
  const hidden = isPosted && shouldHideDone(msId, phId)

  const name = f['Full Deliverable Name'] || f['Name'] || 'Untitled'
  const resp = f['Responsible (Text)'] || ''
  const lft = f['Latest Finish (LFT)'] || ''
  const da = daysFrom(lft)
  const rawFloat = parseFloat(f['Slack'] || 0)
  const completed = parseInt(f['Completed Checklist Items'] || 0)
  const total = completed + parseInt(f['Incomplete Checklist Items'] || 0)
  const isPinned = f['Date Pinned'] === true
  const urgency = f['Urgency Message'] || ''

  // Slack discussion fields
  const threadTs   = f['Slack Thread Timestamp'] || ''
  const projName   = f['Project Name'] || ''
  const lastReadTs = threadTs ? localStorage.getItem('slack:read:' + msId) : null
  const hasUnread  = !!(threadTs && lastReadTs !== threadTs)

  let urgColor = 'var(--aj-text-muted)', urgTxt = ''
  if (urgency.indexOf('IMPOSSIBLE') !== -1) { urgColor = '#c0392b'; urgTxt = 'Impossible' }
  else if (da !== null && da < 0) { urgColor = '#c0392b'; urgTxt = Math.abs(da) + 'd overdue' }
  else if (urgency.indexOf('URGENT') !== -1 || (da !== null && da <= 2)) { urgColor = '#b8540a'; urgTxt = 'Urgent' }
  else if (urgency.indexOf('HEATING') !== -1) { urgColor = '#b8540a'; urgTxt = 'Soon' }

  const dateClass = da !== null && da < 0 ? ' wf-date-btn--red' : da !== null && da < 3 ? ' wf-date-btn--amber' : ''

  return (
    <div
      className={[
        'wf-node wf-node--ms',
        isDirty ? 'wf-node--dirty' : '',
        isPosted ? 'wf-node--posted' : '',
        readOnly ? 'wf-node--ms-readonly' : '',
        isBlockedMs ? 'wf-node--ms-blocked' : '',
        !isPosted && isCollapsed ? 'wf-node--collapsed' : '',
        hidden ? 'wf-hidden-done' : '',
      ].filter(Boolean).join(' ')}
      data-key={'ms:' + msId}
    >
      <div className="wf-row">
        <button
          className="wf-triangle wf-triangle--ms"
          data-fn="toggleCollapse"
          data-key={'ms:' + msId}
        >
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--ms"
          title="Focus on this milestone"
          data-fn="zoomTo"
          data-level="ms"
          data-msid={msId}
          data-pid={phId || ''}
          data-projid={projId || undefined}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--ms">
            {isBlockedMs && <BlockedLock waitingOn={waitingOn} />}{labelPrefix}{name}
          </span>
          {isBlockedMs && (
            <span className="wf-blocked-tag" title="Visible for information only for now.">
              waiting on {waitingOn.map(msLabel).join(', ')}
            </span>
          )}
          {!isPosted && <StartedAheadTag msId={msId} />}
          {isPosted && <PostRecordTag msId={msId} />}
          <CrewInline kind="milestone" id={msId} />
          <span className="wf-meta">
            {isPosted ? (
              <span className="wf-date-btn" style={{ color: '#1D9E75' }}>Posted {fmtDate(f['Posted Date'])}</span>
            ) : (<>
              {/* Dates are edited only in the Gantt scheduler (single editing surface).
                  Clicking the date opens the modal scoped to this project. */}
              <span
                className={`wf-date-btn${dateClass}`}
                style={{ cursor: 'pointer' }}
                title="Edit in Gantt scheduler"
                onClick={e => {
                  e.stopPropagation()
                  window.dispatchEvent(new CustomEvent('open-gantt-modal', { detail: { projectId: projId || wf.zoomProjectId } }))
                }}
              >
                {isPinned && (
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" style={{ marginRight: 3, opacity: 0.6 }}>
                    <path d="M9.5 1.5l5 5-2 1-3-3-4 4 1 1-3 5-2-2 5-3 1 1 4-4-3-3 1-2z"/>
                  </svg>
                )}
                {lft ? fmtDate(lft) : '--'}
              </span>
              <span className="wf-float" style={{ color: rawFloat < 0 ? '#c0392b' : rawFloat < 3 ? '#b8540a' : 'var(--aj-text-muted)' }}>
                {rawFloat < 0 ? '0d' : Math.round(rawFloat) + 'd'}
              </span>
              {total > 0 && (
                <span className={`wf-prog${completed === total ? ' wf-prog--done' : ''}`}>{completed}/{total}</span>
              )}
              {urgTxt && <span className="wf-urg-tag" style={{ color: urgColor }}>{urgTxt}</span>}
              {!resp && <span className="wf-flag wf-flag--red">No responsible</span>}
              {isDirty && <span className="wf-dirty-dot"></span>}
            </>)}
          </span>
          {/* Slack discussion indicator — visible on hover (always visible when thread exists) */}
          <button
            className={`wf-slack-btn${threadTs ? ' wf-slack-btn--active' : ''}`}
            title={threadTs ? 'View discussion' : 'Start a discussion in Slack'}
            onClick={e => { e.stopPropagation(); setShowPanel(true) }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            {hasUnread && <span className="wf-slack-unread-dot" />}
          </button>
        </div>
        {/* Always available — even in a closed-out (read-only) phase, where the menu offers
            view/navigation actions only (see buildMenu 'ms' readOnly branch). */}
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="ms"
          data-key={'ms:' + msId}
          data-msid={msId}
          data-pid={phId || ''}
          data-readonly={readOnly ? 'true' : 'false'}
        >&#8943;</button>
      </div>
      {showPanel && (
        <SlackThreadPanel
          entityId={msId}
          entityType="milestone"
          entityName={name}
          projectId={projId || wf.zoomProjectId || ''}
          projectName={projName}
          threadTs={threadTs}
          onClose={() => setShowPanel(false)}
          onThreadStarted={ts => {
            const ms = ctx.state?.milestones?.find(m => m.id === msId)
            if (ms) ms.fields['Slack Thread Timestamp'] = ts
          }}
        />
      )}
      {!isCollapsed && (
        <div className="wf-children">
          <MilestoneSections msId={msId} ms={ms} readOnly={readOnly} />
        </div>
      )}
    </div>
  )
}

export function PhaseRow({ ph, milestones, onTimeToggle, projId }) {
  const f = ph.fields
  const phId = ph.id
  const name = f['Phase Name'] || f['Name'] || 'Phase'
  const allowTime = f['Allow Time Entries'] === true
  const isClosedOut = f['Phase Closed Out'] === true
  const isAS = !!(f['Additional Services Number'])
  const phaseIdStr = f['Project Phase ID'] || ''
  const customTitle = f['Custom Title'] || ''
  const displayName = isAS ? (customTitle || name) : name
  const contracted = parseFloat(f['Contracted Fee'] || 0)
  const expended = parseFloat(f['Fee Expended'] || 0)
  const remaining = parseFloat(f['Fee Remaining'] || 0)
  const ebr = parseFloat(f['Effective Billing Rate'] || 0)

  const truePct = contracted > 0 ? Math.round(expended / contracted * 100) : 0
  const overBudget = truePct > 100
  const displayPct = Math.min(truePct, 100)
  const feeColor = overBudget ? '#c0392b' : truePct > 90 ? '#b8540a' : '#1D9E75'

  const phaseMilestones = getMsForPhase(ph, milestones)

  // Hide closed-out phases when "active only" filter is on
  if (isClosedOut && wf.hideDoneKeys?.global) return null

  // Command filter: hide phases that have no matching milestones
  const cmdFilter = wf.cmdFilter
  if (cmdFilter?.type === 'msIds') {
    if (!phaseMilestones.some(m => cmdFilter.msIds.has(m.id))) return null
  }

  const activeMsCount = phaseMilestones.filter(m => !m.fields['Posted Date']).length
  const timeOpenNoWork = allowTime && activeMsCount === 0
  // When a filter is active, force expand so matching milestones are visible
  const isCollapsed = cmdFilter?.type === 'msIds' ? false : (wf.collapsed['ph:' + phId] !== false)

  return (
    <div
      className={`wf-node wf-node--phase${isCollapsed ? ' wf-node--collapsed' : ''}${isClosedOut ? ' wf-node--phase-closed' : ''}`}
      data-key={'ph:' + phId}
    >
      <div className="wf-row">
        <button
          className="wf-triangle"
          data-fn="toggleCollapse"
          data-key={'ph:' + phId}
        >
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--phase"
          title="Focus on this phase"
          data-fn="zoomTo"
          data-level="phase"
          data-pid={phId}
          data-projid={projId || undefined}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--phase">
            {phaseIdStr && <span className="wf-phase-id">{phaseIdStr}</span>}
            {displayName}
          </span>
          <span className="wf-meta">
            {isClosedOut && (
              <span className="wf-badge wf-badge--gray wf-phase-closed-badge">Closed</span>
            )}
            {!isClosedOut && contracted > 0 && <>
              <span className="wf-fee-bar-wrap">
                <span className="wf-fee-bar-fill" style={{ width: displayPct + '%', background: feeColor }}></span>
              </span>
              <span className="wf-fee-pct" style={{ color: feeColor }}>{truePct}% fee</span>
              {overBudget
                ? <span className="wf-fee-pct" style={{ color: '#c0392b' }}>${Math.round((expended - contracted) / 1000)}k over</span>
                : remaining > 0 && <span className="wf-fee-rem">${Math.round(remaining / 1000)}k left</span>
              }
              {ebr > 0 && <span className="wf-fee-rem">EBR ${Math.round(ebr)}</span>}
            </>}
            {!isClosedOut && (
              <label className="wf-inline-toggle" title={allowTime ? 'Disable time entries' : 'Enable time entries'}>
                <input
                  type="checkbox"
                  className="wf-inline-toggle-inp"
                  checked={allowTime}
                  onChange={() => onTimeToggle(phId, allowTime)}
                />
                <span className="wf-inline-toggle-track"><span className="wf-inline-toggle-thumb"></span></span>
              </label>
            )}
            {!isClosedOut && timeOpenNoWork && (
              <span className="wf-phase-signal wf-phase-signal--amber">Time open, no active work</span>
            )}
          </span>
        </div>
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="phase"
          data-key={'ph:' + phId}
          data-pid={phId}
          data-allowtime={String(allowTime)}
          data-closedout={String(isClosedOut)}
          data-isas={String(isAS)}
        >&#8943;</button>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          <PhaseSections ph={ph} milestones={milestones} projId={projId} readOnly={isClosedOut} />
        </div>
      )}
    </div>
  )
}

function calcScheduleVis() {
  if (!wf.zoomProjectId) return 'hidden'
  // Schedule root zoom: focused on Schedule, no phase/ms/section selected
  if (wf.zoomRoot === 'schedule' && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'rootZoom'
  // Phase sub-section zoom: sec:phase:{phId}:{milestones|threads} or
  // thread category zoom sec:phase:{phId}:thcat:{Type}
  if (wf.zoomSection && wf.zoomSection.indexOf('sec:phase:') === 0 && !wf.zoomMsId &&
      !wf.zoomCat && !wf.zoomItemId && !wf.searchQuery) {
    if (wf.zoomSection.indexOf(':thcat:') !== -1) return 'phaseThreadCatZoom'
    const t = wf.zoomSection.slice(wf.zoomSection.lastIndexOf(':') + 1)
    return t === 'threads' ? 'phaseThreadsZoom' : 'phaseMilestonesZoom'
  }
  // Phase zoom: a phase is selected but no milestone/section yet
  if (wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.searchQuery) return 'phaseZoom'
  // Home: no zoom active at all
  if (!wf.zoomRoot && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'home'
  return 'hidden'
}

export default function ScheduleSection() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const [confirmCloseOutId, setConfirmCloseOutId] = useState(null)
  const [confirmReopenId, setConfirmReopenId] = useState(null)
  const [reopenEnableTime, setReopenEnableTime] = useState(false)
  const [editTitlePhId, setEditTitlePhId] = useState(null)
  const [editTitleValue, setEditTitleValue] = useState('')

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  useEffect(() => {
    function onCloseOutConfirm(e) { setConfirmCloseOutId(e.detail?.phId || null) }
    function onReopen(e) {
      const phId = e.detail?.phId
      if (!phId) return
      setReopenEnableTime(false)
      setConfirmReopenId(phId)
    }
    function onEditTitle(e) {
      const phId = e.detail?.phId
      if (!phId) return
      const ph = ctx.state?.phases?.find(p => p.id === phId)
      setEditTitleValue(ph?.fields?.['Custom Title'] || '')
      setEditTitlePhId(phId)
    }
    window.addEventListener('phase-closeout-confirm', onCloseOutConfirm)
    window.addEventListener('phase-reopen', onReopen)
    window.addEventListener('phase-edit-title', onEditTitle)
    return () => {
      window.removeEventListener('phase-closeout-confirm', onCloseOutConfirm)
      window.removeEventListener('phase-reopen', onReopen)
      window.removeEventListener('phase-edit-title', onEditTitle)
    }
  }, [])

  // Ensure threads are loaded when landing directly on a phase Threads zoom
  // (page refresh / deep link) — runs as a cheap guard on every render.
  useEffect(() => {
    const v = calcScheduleVis()
    if (v === 'phaseThreadsZoom' || v === 'phaseThreadCatZoom') ensureThreadsLoaded(wf.zoomProjectId)
  })

  const vis = calcScheduleVis()
  if (vis === 'hidden') return null

  const phases = ctx.state?.phases || []
  const milestones = ctx.state?.milestones || []

  function handleTimeToggle(phId, currentValue) {
    if (ctx.state?.phases) {
      const ph = ctx.state.phases.find(p => p.id === phId)
      if (ph) ph.fields['Allow Time Entries'] = !currentValue
    }
    forceUpdate()
    updatePhaseTimeEntries(phId, !currentValue)
  }

  function handleCloseOutConfirm(phId) {
    // Gate: a phase can't be closed until every milestone in it is posted.
    const gatePh = (ctx.state?.phases || []).find(p => p.id === phId)
    const unposted = (gatePh ? getMsForPhase(gatePh, ctx.state?.milestones || []) : []).filter(m => !m.fields['Posted Date'])
    if (unposted.length) {
      setConfirmCloseOutId(null)
      alertDialog({ title: 'Can’t close this phase yet', message: 'Every milestone must be posted before closing the phase. Still open: ' + unposted.map(m => (m.fields['Name (from Deliverable)']?.[0] || m.fields['Subtitle'] || m.fields['Name'] || 'a milestone')).join(', ') + '.' })
      return
    }
    setConfirmCloseOutId(null)
    if (ctx.state?.phases) {
      const ph = ctx.state.phases.find(p => p.id === phId)
      if (ph) { ph.fields['Phase Closed Out'] = true; ph.fields['Allow Time Entries'] = false }
    }
    forceUpdate()
    closeOutPhase(phId)
  }

  function handleSaveCustomTitle(phId, title) {
    setEditTitlePhId(null)
    if (ctx.state?.phases) {
      const ph = ctx.state.phases.find(p => p.id === phId)
      if (ph) ph.fields['Custom Title'] = title
    }
    forceUpdate()
    updatePhaseCustomTitle(phId, title)
  }

  function handleReopenConfirm(phId, allowTime) {
    setConfirmReopenId(null)
    if (ctx.state?.phases) {
      const ph = ctx.state.phases.find(p => p.id === phId)
      if (ph) {
        ph.fields['Phase Closed Out'] = false
        if (allowTime) ph.fields['Allow Time Entries'] = true
      }
    }
    forceUpdate()
    reopenPhase(phId, allowTime)
  }

  // Confirmation modal for close-out
  const closeOutModal = confirmCloseOutId ? (() => {
    const confirmPh = phases.find(p => p.id === confirmCloseOutId)
    const confirmPhName = confirmPh?.fields?.['Phase Name'] || 'this phase'
    return (
      <div className="wf-closeout-overlay" onClick={() => setConfirmCloseOutId(null)}>
        <div className="wf-closeout-modal" onClick={e => e.stopPropagation()}>
          <div className="wf-closeout-title">Close out {confirmPhName}?</div>
          <div className="wf-closeout-body">
            This marks the phase as complete and locks it from further edits.
            Time entries will be disabled, and the phase will be hidden when
            filtering to active work.
          </div>
          <div className="wf-closeout-actions">
            <button className="wf-closeout-btn wf-closeout-btn--cancel" onClick={() => setConfirmCloseOutId(null)}>Cancel</button>
            <button className="wf-closeout-btn wf-closeout-btn--confirm" onClick={() => handleCloseOutConfirm(confirmCloseOutId)}>Close Out Phase</button>
          </div>
        </div>
      </div>
    )
  })() : null

  // Reopen confirmation modal (with optional time-entry toggle)
  const reopenModal = confirmReopenId ? (() => {
    const reopenPh = phases.find(p => p.id === confirmReopenId)
    const reopenPhName = reopenPh?.fields?.['Phase Name'] || 'this phase'
    return (
      <div className="wf-closeout-overlay" onClick={() => setConfirmReopenId(null)}>
        <div className="wf-closeout-modal" onClick={e => e.stopPropagation()}>
          <div className="wf-closeout-title">Reopen {reopenPhName}?</div>
          <div className="wf-closeout-body">
            The phase will be unlocked for editing and visible when filtering to active work.
          </div>
          <label className="wf-closeout-time-row">
            <input
              type="checkbox"
              className="wf-closeout-time-cb"
              checked={reopenEnableTime}
              onChange={e => setReopenEnableTime(e.target.checked)}
            />
            <span>Enable time entries</span>
          </label>
          <div className="wf-closeout-actions">
            <button className="wf-closeout-btn wf-closeout-btn--cancel" onClick={() => setConfirmReopenId(null)}>Cancel</button>
            <button className="wf-closeout-btn wf-closeout-btn--confirm" onClick={() => handleReopenConfirm(confirmReopenId, reopenEnableTime)}>Reopen Phase</button>
          </div>
        </div>
      </div>
    )
  })() : null

  // Edit custom title modal (Additional Services phases)
  const editTitleModal = editTitlePhId ? (() => {
    const etPh = phases.find(p => p.id === editTitlePhId)
    const etPhName = etPh?.fields?.['Phase Name'] || 'phase'
    return (
      <div className="wf-closeout-overlay" onClick={() => setEditTitlePhId(null)}>
        <div className="wf-closeout-modal" onClick={e => e.stopPropagation()}>
          <div className="wf-closeout-title">Edit title — {etPhName}</div>
          <input
            className="wf-edit-title-input"
            type="text"
            value={editTitleValue}
            onChange={e => setEditTitleValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveCustomTitle(editTitlePhId, editTitleValue)
              if (e.key === 'Escape') setEditTitlePhId(null)
            }}
            placeholder="Custom title…"
            autoFocus
          />
          <div className="wf-closeout-actions">
            <button className="wf-closeout-btn wf-closeout-btn--cancel" onClick={() => setEditTitlePhId(null)}>Cancel</button>
            <button className="wf-closeout-btn wf-closeout-btn--confirm" onClick={() => handleSaveCustomTitle(editTitlePhId, editTitleValue)}>Save</button>
          </div>
        </div>
      </div>
    )
  })() : null

  // Phase zoom: show the two phase sub-section nodes (Milestones, Threads)
  if (vis === 'phaseZoom') {
    const zoomedPhase = phases.find(p => p.id === wf.zoomPhaseId)
    const pf = zoomedPhase?.fields || {}
    const zoomedPhaseClosedOut = pf['Phase Closed Out'] === true
    return (
      <>
        <div className="wf-list">
          {/* Time-entries toggle now lives inline on the zoom title (ZoomTitle) */}
          {zoomedPhase && (
            <PhaseSections ph={zoomedPhase} milestones={milestones} readOnly={zoomedPhaseClosedOut} />
          )}
        </div>
        {closeOutModal}
        {reopenModal}
        {editTitleModal}
      </>
    )
  }

  // Phase Milestones section zoom: children-only milestone rows
  if (vis === 'phaseMilestonesZoom' || vis === 'phaseThreadsZoom') {
    const secPhId = (wf.zoomSection || '').split(':')[2] || wf.zoomPhaseId
    const zoomedPhase = phases.find(p => p.id === secPhId)
    const pf = zoomedPhase?.fields || {}
    const zoomedPhaseClosedOut = pf['Phase Closed Out'] === true
    if (!zoomedPhase) return <div className="wf-list"><div className="wf-loading">Loading…</div></div>
    if (vis === 'phaseMilestonesZoom') {
      const phaseMilestones = getMsForPhase(zoomedPhase, milestones)
      return (
        <>
          <div className="wf-list">
            {phaseMilestones.map(ms => (
              <MilestoneRow key={ms.id} ms={ms} phId={secPhId} readOnly={zoomedPhaseClosedOut} />
            ))}
          </div>
          {closeOutModal}
          {reopenModal}
          {editTitleModal}
        </>
      )
    }
    // phaseThreadsZoom — children-only category nodes
    return (
      <div className="wf-list">
        <PhaseThreadsContent ph={zoomedPhase} milestones={milestones} projId={wf.zoomProjectId} />
      </div>
    )
  }

  // Phase thread-category zoom: children-only thread rows of one Type
  if (vis === 'phaseThreadCatZoom') {
    const secPhId = (wf.zoomSection || '').split(':')[2] || wf.zoomPhaseId
    const catType = (wf.zoomSection || '').slice((wf.zoomSection || '').lastIndexOf(':') + 1)
    const zoomedPhase = phases.find(p => p.id === secPhId)
    const projectName = milestones?.[0]?.fields?.['Project Name'] || ''
    const threads = ctx.threadCache
    const list = (zoomedPhase && Array.isArray(threads))
      ? threadsForPhase(threads, zoomedPhase).filter(t => (t.fields['Type'] || 'Misc. Items') === catType)
      : null
    return (
      <div className="wf-list">
        {!list && <FillerRow text="Loading…" />}
        {list && list.length === 0 && <FillerRow text="No threads in this category." italic />}
        {list && list.map(t => (
          <ThreadNode key={t.id} item={t} projectId={wf.zoomProjectId} projectName={projectName} />
        ))}
      </div>
    )
  }

  // Schedule root zoom: show all phases (no root wrapper)
  if (vis === 'rootZoom') {
    return (
      <>
        <div className="wf-list">
          {phases.map(ph => (
            <PhaseRow
              key={ph.id}
              ph={ph}
              milestones={milestones}
              onTimeToggle={handleTimeToggle}
            />
          ))}
        </div>
        {closeOutModal}
        {reopenModal}
        {editTitleModal}
      </>
    )
  }

  // Home: full root node + collapsible phases
  const rootCollapsed = wf.collapsed['root:schedule'] !== false
  return (
    <>
      <div className="wf-list">
        <div
          className={`wf-node wf-node--root wf-node--root-schedule${rootCollapsed ? ' wf-node--collapsed' : ''}`}
          data-key="root:schedule"
        >
          <div className="wf-row">
            <button
              className="wf-triangle"
              data-fn="toggleCollapse"
              data-key="root:schedule"
            >
              <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
            </button>
            <button
              className="wf-bullet wf-bullet--root"
              title="Focus on Schedule"
              data-fn="zoomTo"
              data-level="root"
              data-rootname="schedule"
            ></button>
            <div className="wf-text-wrap">
              <span className="wf-node-text wf-node-text--root">Schedule</span>
            </div>
            <button
              className="wf-menu-btn"
              data-fn="openMenu"
              data-type="root"
              data-key="root:schedule"
              data-rootname="schedule"
            >&#8943;</button>
          </div>
          {!rootCollapsed && (
            <div className="wf-children">
              {phases.map(ph => (
                <PhaseRow
                  key={ph.id}
                  ph={ph}
                  milestones={milestones}
                  onTimeToggle={handleTimeToggle}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {closeOutModal}
      {reopenModal}
      {editTitleModal}
    </>
  )
}
